import React, { useEffect } from "react";
import { withRouter } from "react-router-dom";
import { matchRoutes } from "react-router-config";
import { connect } from "react-redux";

import { asideRepositoryNavigationByModule } from "../Repository/navigation";

import styles from "./App.module.sass";

import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    downloadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    setDataFileStatusAction,
    deleteFilePermanentlyAction,
    deleteInvalidFilePermanentlyAction,
    hideFileAction,
    downloadFileAction,
    reprocessRepositoryAction,
    genericSearchAction,
    getFindingDetails,
    reprocessFileAction,
    getDownloadTemplateData,
    downloadCsvStreamAction,
} from "../../store/actions";

import proxy from "../../routes/proxy";

import { changeDeepObject } from "../../utils";
import { redirectNotValid, buildStaticLinks, applyThemeWithPriority } from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import useUpdateThemes from "../../hooks/useUpdateThemes";

import {
    appRedirectValidations,
    controllerQueries,
    generateChildren,
    getQueryResultsArray,
    onClickConfiguratorApp,
    isPageLoading,
    GlobalPageLayout,
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    buildDisplayComponents,
    buildHeaderComponents,
    headerTitle,
    getDownloadableQueries,
    generateNavigation,
    buildNavProps,
} from "../utils/utils";

const App = (props) => {
    const {
        pushModal,
        modules,
        fetch,
        match,
        route,
        location,
        history,
        reload,
        matchParams,
        projectId,
        modulePrefix,
        locale,
        fetchResults,
        fetchStatus,
        appContext,
        auth,
        userInformation,
        i18n,
    } = useControllerInitialization(props);

    let { fiscalYear, importId, section, fileType } = matchParams;

    const {
        menuOrganizationProject,
        organizationProjects,
        projectFiscalYears,
        project,
        fiscalYears,
        fiscalYearsWithImportIds,
        importIdsFromUrlFiscalYear,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus: organizationProjectQueryStatus,
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: App,
        matchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
    });

    const { loading = true, success = false } = organizationProjectQueryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);
    // Apply theme with priority logic: Project > Organization > Default
    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "App");
    }, [organizationProjects, projectId, updateThemes]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    //############ Build Generic Menu  ############
    const { navData, importIdToUse, routeQueries, params, firstModuleFromProject } =
        buildGenericMenu(
            modules,
            fetchResults,
            locale,
            fiscalYear,
            projectId,
            importId,
            project,
            location,
            matchParams,
            route,
        );

    const {
        repositoryStatus = { status: 1 },
        getTotalExportLines = "",
        genericOverview,
    } = getQueryResultsArray(fetchResults, [
        {
            resultName: "repositoryStatus",
            queryName: `${locale}_repository_status_fiscalYear_${fiscalYear}_projectId_${projectId}`,
        },
        {
            resultName: "getTotalExportLines",
            queryName: `${locale}_get_limit_export`,
        },
        {
            resultName: "genericOverview",
            queryName: `${locale}_generic_overview_fiscalYear_${fiscalYear}_importId_${importId}_locale_${locale}_modulePrefix_${modulePrefix}_projectId_${projectId}`,
        },
    ]);

    const getLayout = JSON.parse(genericOverview || "[]");

    changeDeepObject(getLayout, ["component", "cards"], {
        SubmitSalesInfo: { projectData: projectFiscalYears },
    });

    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const displayComponents = buildDisplayComponents(route, getLayout);

    const children = generateChildren({
        displayComponents,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        modules,
        fetch,
        pushModal,
        reload,
        onClickConfigurator: onClickConfiguratorApp,
    });

    const title = headerTitle(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        fiscalYear,
        importId,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: App,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const redirect = redirectNotValid(userInformation, locale, false, auth);

    // function to make the correct redirects in the app
    const appRedirect = appRedirectValidations({
        redirect,
        projectId,
        fiscalYear,
        importId,
        fiscalYears,
        appContext,
        match,
        matchParams,
        importIdToUse,
        firstModuleFromProject,
        section,
        fileType,
    });

    if (appRedirect) {
        return appRedirect;
    }

    const { navProps, staticLinks } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: { ...params, fiscalYear, importId, viewMode: null },
        repositoryStatus,
        getTotalExportLines,
        staticLinks,
        styles,
        organizationProjects
    };

    return (
        LoadingComponent || (
            <GlobalPageLayout
                styles={styles}
                globalLayoutProps={globalLayoutProps}
                modules={modules}
                headerProps={{
                    genericSearchAction: true,
                    reportsAction: true,
                    fiscalYears: fiscalYearsWithImportIds,
                    importIds: appContext === "repository" ? "" : importIdsFromUrlFiscalYear,
                    downloadableQueries,
                    title:
                        (title && Array.isArray(title) ? title.join(" ") : title) ||
                        i18n`${params.section || ""}`,
                    totalLinesCsv: getTotalExportLines,
                    headerComponents: headerComponents,
                    onViewModeChange: null,
                    viewMode: null,
                    titleBarActions:
                        headerComponents.length > 0
                            ? headerComponents.map((headerComponent, index) => (
                                  <headerComponent.Component
                                      key={`titlebar-action-${index}`}
                                      download={downloadAction}
                                      downloadableQueries={downloadableQueries}
                                      locale={locale}
                                      user={userInformation}
                                      items={route?.headerComponentConfigs?.items || []}
                                      label={i18n`${route?.headerComponentConfigs.label}`}
                                      {...headerComponent.props}
                                  />
                              ))
                            : undefined,
                }}
                headerComponentsMapper={(downloading) => (headerComponentProperties) => (
                    <headerComponentProperties.Component
                        download={downloadAction}
                        downloading={downloading}
                        downloadableQueries={downloadableQueries}
                        locale={locale}
                        user={userInformation}
                        items={route?.headerComponentConfigs?.items || []}
                        label={i18n`${route?.headerComponentConfigs.label}`}
                        {...headerComponentProperties.props}
                    />
                )}
                fetch={fetch}
                route={route}
                matchParams={matchParams}
                isLoadingContent={(fetch) => isPageLoading(fetch?.status, fetch?.activity)}
            >
                {children}
            </GlobalPageLayout>
        )
    );
};

App.queries = (args, userModulesByProject) => {
    return controllerQueries(args, userModulesByProject);
};

export default connect(
    ({ user, fetch, menu, uploads, auditNote }) => ({
        user,
        fetch,
        menu,
        uploads,
        auditNote,
    }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
        getFindingDetails,
        setDataFileStatus: setDataFileStatusAction,
        deleteFilePermanently: deleteFilePermanentlyAction,
        deleteInvalidFilePermanently: deleteInvalidFilePermanentlyAction,
        hideFile: hideFileAction,
        downloadFile: downloadFileAction,
        reprocessRepository: reprocessRepositoryAction,
        uploadFiles: uploadAction,
        download: downloadAction,
        submitAuditNote: auditNoteSubmit,
        genericSearchAction,
        reprocessFiles: reprocessFileAction,
        getDownloadTemplateData,
        downloadCsvStreamAction,
    },
)(withRouter(App));
